import {
  Account,
  AccountUser,
  Club,
  ClubPlayer,
  FundingAppeal,
  FundingAppealEmailTemplate,
  FundingAppealLandingPage,
  FundingOutreach,
  FundingOutreachCommunication,
  FundingOutreachCommunicationAnalysis,
  Funder,
  fundingOutreachShouldAdvanceToReplied,
  TournamentSession,
} from "@mixitone/models";
import { List, subscribeList, ValidationError } from "@mixitone/oom";
import { FormController } from "components/Forms/FormController";
import type { FormFieldConfig } from "components/Forms/FormFieldsDefinition";
import { actions } from "@mixitone/actions";
import {
  applyFundingLetterTemplate,
  formatCurrencyCents,
  fundingAppealLandingPageDirectoryUrl,
  withStateToggle,
} from "@mixitone/util";
import { captureException } from "@mixitone/telemetry";
import { FUNDER_TYPE_SELECT_OPTIONS, FUNDING_OUTREACH_STATUS_SELECT_OPTIONS } from "lib/fundingStatusLabels";
import { flushTauriSyncAndWait } from "lib/tauri/sync";

/** Select value used when creating a new funder in the same flow (shared with appeal add-funder modal). */
export const ADD_NEW_FUNDER_VALUE = "__new__";

/** Realistic funder reply templates for dev simulation – exercise sentiment / AI analysis. */
export const SIMULATED_REPLY_TEMPLATES: Array<{
  id: string;
  label: string;
  subject: string;
  bodyPlain: string;
  bodyHtml: string;
}> = [
  {
    id: "positive-interested",
    label: "Positive – interested, requests more info",
    subject: "Re: Funding request",
    bodyPlain: `Thank you for your application. We are interested in supporting your project and would like to request some additional information before our panel meets.

Could you please send us your latest accounts and a short breakdown of how the grant would be used by the end of the month? We aim to notify successful applicants within six weeks.

Best regards,
Grants Team`,
    bodyHtml: `<p>Thank you for your application. We are interested in supporting your project and would like to request some additional information before our panel meets.</p>
<p>Could you please send us your latest accounts and a short breakdown of how the grant would be used by the end of the month? We aim to notify successful applicants within six weeks.</p>
<p>Best regards,<br/>Grants Team</p>`,
  },
  {
    id: "positive-award",
    label: "Positive – award confirmed",
    subject: "Re: Funding request",
    bodyPlain: `I'm pleased to confirm that our grants panel has approved your application for £2,500.

We will send the payment within 10 working days. Please acknowledge receipt and use the funds as set out in your application. We wish you every success with the project.

Kind regards`,
    bodyHtml: `<p>I'm pleased to confirm that our grants panel has approved your application for £2,500.</p>
<p>We will send the payment within 10 working days. Please acknowledge receipt and use the funds as set out in your application. We wish you every success with the project.</p>
<p>Kind regards</p>`,
  },
  {
    id: "negative-declined",
    label: "Negative – declined, no reason",
    subject: "Re: Your funding enquiry",
    bodyPlain: `Thank you for your interest. Unfortunately we are unable to support your application on this occasion. We wish you well in securing funding elsewhere.`,
    bodyHtml: `<p>Thank you for your interest. Unfortunately we are unable to support your application on this occasion. We wish you well in securing funding elsewhere.</p>`,
  },
  {
    id: "negative-not-eligible",
    label: "Negative – not eligible",
    subject: "Re: Funding request",
    bodyPlain: `Thank you for getting in touch. After reviewing your application we have concluded that your project does not fall within our current funding criteria, which prioritise community sport and youth participation. We are therefore unable to take your application further. We encourage you to look at our website for other programmes that may be a better fit.`,
    bodyHtml: `<p>Thank you for getting in touch. After reviewing your application we have concluded that your project does not fall within our current funding criteria, which prioritise community sport and youth participation. We are therefore unable to take your application further.</p>
<p>We encourage you to look at our website for other programmes that may be a better fit.</p>`,
  },
  {
    id: "neutral-holding",
    label: "Neutral – holding, will decide later",
    subject: "Re: Funding request",
    bodyPlain: `We have received your application and it is currently with our grants team. We have a high volume of applications this round and will be in touch by the end of next month with a decision. No further action is required from you at this stage.`,
    bodyHtml: `<p>We have received your application and it is currently with our grants team. We have a high volume of applications this round and will be in touch by the end of next month with a decision.</p>
<p>No further action is required from you at this stage.</p>`,
  },
  {
    id: "neutral-clarification",
    label: "Neutral – request for clarification",
    subject: "Re: Funding request",
    bodyPlain: `Thanks for your application. We need one clarification before we can assess it: could you confirm whether the project will be delivered in the current financial year? Once we have this we will add your application to the next panel meeting.`,
    bodyHtml: `<p>Thanks for your application. We need one clarification before we can assess it: could you confirm whether the project will be delivered in the current financial year?</p>
<p>Once we have this we will add your application to the next panel meeting.</p>`,
  },
  {
    id: "positive-mentions-amount",
    label: "Positive – mentions possible amount (dev)",
    subject: "Re: Funding request",
    bodyPlain: `Thank you for getting in touch. We may be able to support your project with a grant of $250. Could you send us a short breakdown of how you would use the funds? We will confirm after our next panel meeting.`,
    bodyHtml: `<p>Thank you for getting in touch. We may be able to support your project with a grant of $250.</p>
<p>Could you send us a short breakdown of how you would use the funds? We will confirm after our next panel meeting.</p>`,
  },
];

interface Props {
  clubId: string;
  id?: string;
  appealId?: string;
  tab?: "details" | "messages";
  attributes?: Attributes;
  onSubmit?: (record: FundingOutreach) => Promise<void>;
}

interface State {
  funders: Funder[];
  /** Account users for the current record's account (for Assigned to dropdown). */
  accountUsers: AccountUser[];
  communications: List<FundingOutreachCommunication>;
  sendingEmail: boolean;
  /** True while simulating an inbound reply (dev only). */
  simulatingReply: boolean;
  /** Analysis for the latest inbound reply (for drawer summary) */
  latestReplyAnalysis: FundingOutreachCommunicationAnalysis | null;
  /** Sentiment/summary per inbound communication id (for thread display) */
  analysesByCommId: Record<string, { sentiment: string; summary: string | null }>;
  /** UI: compose modal and draft */
  showComposeModal: boolean;
  composeSubject: string;
  composeBody: string;
  composeMergeValues: Record<string, string>;
  /** UI: which communication is shown in the detail modal */
  viewingCommunication: FundingOutreachCommunication | null;
  /** UI: reply composer shown inside view modal (keeps message visible above) */
  replyingInViewModal: boolean;
  /** UI: show post-submit notice when send is deferred (e.g. AI flag / bulk) */
  pendingReviewMessage: boolean;
  /** Saved templates for the appeal (compose modal) */
  emailTemplatesForCompose: FundingAppealEmailTemplate[];
  /** When set, a successful send locks this template server-side; null = one-off */
  composeFundingAppealEmailTemplateId: string | null;
  /** UI: accept amount modal and input */
  showAcceptAmountModal: boolean;
  acceptAmountPounds: string;
  /** UI: dev simulate-reply template index */
  simulateTemplateIndex: number;
  /** UI: add manual message (note / phone / SMS / pasted email) */
  showAddMessageModal: boolean;
  addMessageSource: "phone" | "sms" | "pasted" | "other";
  addMessageSubject: string;
  addMessageBody: string;
  addingMessage: boolean;
  /** Inline funder form inside outreach drawer (same pattern as session player → club player). */
  editingFunder: boolean;
  /** Funder ids that already have an outreach row on this appeal (add-funder modal). */
  funderIdsAlreadyOnAppeal: string[];
  /** Multi-select on “Select funders” tab of add-funder modal. */
  bulkSelectedFunderIds: string[];
}

export const MANUAL_MESSAGE_SOURCE_LABELS: Record<State["addMessageSource"], string> = {
  phone: "Phone call",
  sms: "SMS",
  pasted: "Pasted email",
  other: "Other",
};

type Attributes = object;

class FundingOutreachFormController extends FormController<FundingOutreach, Attributes, State, Props> {
  static override initialState = {
    ...FormController.initialState,
    funders: [] as Funder[],
    accountUsers: [] as AccountUser[],
    communications: new List<FundingOutreachCommunication>(),
    sendingEmail: false,
    simulatingReply: false,
    latestReplyAnalysis: null as FundingOutreachCommunicationAnalysis | null,
    analysesByCommId: {} as Record<string, { sentiment: string; summary: string | null }>,
    showComposeModal: false,
    composeSubject: "",
    composeBody: "",
    composeMergeValues: {} as Record<string, string>,
    viewingCommunication: null as FundingOutreachCommunication | null,
    replyingInViewModal: false,
    pendingReviewMessage: false,
    emailTemplatesForCompose: [] as FundingAppealEmailTemplate[],
    composeFundingAppealEmailTemplateId: null as string | null,
    showAcceptAmountModal: false,
    acceptAmountPounds: "",
    simulateTemplateIndex: 0,
    showAddMessageModal: false,
    addMessageSource: "phone" as const,
    addMessageSubject: "",
    addMessageBody: "",
    addingMessage: false,
    editingFunder: false,
    funderIdsAlreadyOnAppeal: [] as string[],
    bulkSelectedFunderIds: [] as string[],
  };

  override async buildRecord(): Promise<FundingOutreach> {
    const record = new FundingOutreach({
      club_id: this.props.clubId,
      account_id: Account.current!.id!,
      status: "to_contact",
      funder_id: "",
      funding_appeal_id: this.props.appealId ?? null,
    }) as FundingOutreach & {
      new_funder_name?: string;
      new_funder_type?: string;
      new_funder_contact_name?: string;
      new_funder_contact_email?: string;
      new_funder_contact_phone?: string;
      new_funder_note?: string;
    };
    record.new_funder_name = "";
    record.new_funder_type = "council";
    record.new_funder_contact_name = "";
    record.new_funder_contact_email = "";
    record.new_funder_contact_phone = "";
    record.new_funder_note = "";
    return record;
  }

  override async findModel(id: string): Promise<FundingOutreach> {
    const record = await FundingOutreach.query().find(id);
    await record.funder.load();
    await record.funding_appeal?.load();
    await record.created_by_account_user?.load();
    await record.assigned_to_account_user?.load();
    return record;
  }

  actionEditFunder(show: boolean): void {
    this.state.editingFunder = show;
  }

  /** Add-funder modal: use the “new funder” fields without showing the funder dropdown. */
  actionPrepareNewFunderTab(): void {
    const record = this.state.record as FundingOutreach | null;
    if (!record || !this.addFunderOnly) return;
    record.funder_id = ADD_NEW_FUNDER_VALUE;
  }

  actionToggleBulkFunderSelection(funderId: string): void {
    if (this.state.funderIdsAlreadyOnAppeal.includes(funderId)) return;
    const cur = this.state.bulkSelectedFunderIds;
    const i = cur.indexOf(funderId);
    this.state.bulkSelectedFunderIds =
      i >= 0 ? [...cur.slice(0, i), ...cur.slice(i + 1)] : [...cur, funderId];
  }

  @withStateToggle("saving")
  async actionAddSelectedFunders(): Promise<void> {
    if (!this.props.appealId || !this.addFunderOnly) return;
    const ids = [...this.state.bulkSelectedFunderIds];
    if (ids.length === 0) return;
    this.state.errors = {};
    try {
      for (const funderId of ids) {
        const record = await this.buildRecord();
        record.funder_id = funderId;
        await this.saveRecord(record);
      }
      this.state.bulkSelectedFunderIds = [];
      this.actionBack();
    } catch (err) {
      captureException(err);
      this.state.errors = {};
      console.error(err);
      if (err instanceof ValidationError) {
        const first = Object.values(err.errors)[0];
        this.state.errors._bulk = first ?? "Could not add funders";
      } else if (err instanceof Error) {
        this.state.errors._bulk = err.message;
      }
    }
  }

  /** Reload funder from DB (bypasses registry cache so cancel discards unsaved field edits). */
  async actionReloadFunder(): Promise<void> {
    const record = this.state.record as FundingOutreach | null;
    const funderId = record?.funder_id;
    if (!record || !funderId) return;
    const fresh = await Funder.query().find(funderId, { noCache: true });
    (record as FundingOutreach & { funder?: Funder }).funder = fresh;
  }

  @withStateToggle("sendingEmail")
  async actionSendEmail(subject: string, bodyHtml: string): Promise<{ pendingReview?: boolean }> {
    const record = this.state.record as FundingOutreach;
    if (!record?.id) return {};
    const tid = this.state.composeFundingAppealEmailTemplateId;
    const template = tid ? this.state.emailTemplatesForCompose.find((x) => x.id === tid) : null;
    const bodyToSend =
      template?.render_mode === "designer_document"
        ? bodyHtml
        : applyFundingLetterTemplate(bodyHtml, await this.templateValuesForOutreach(record));
    await flushTauriSyncAndWait();
    const result = await actions.funding.sendFundingOutreachEmail({
      outreachId: record.id,
      subject,
      bodyHtml: bodyToSend,
      ...(tid ? { fundingAppealEmailTemplateId: tid } : {}),
    });
    return { pendingReview: result.pendingReview };
  }

  /** Simulate an inbound funder reply by POSTing to `funding-email/inbound` (dev only). */
  @withStateToggle("simulatingReply")
  async actionSimulateReply(templateIndex: number): Promise<void> {
    const record = this.state.record as FundingOutreach | null;
    if (!record?.id) return;
    const template = SIMULATED_REPLY_TEMPLATES[templateIndex];
    if (!template) return;
    const baseUrl = import.meta.env.VITE_SUPABASE_URL;
    const body = new URLSearchParams({
      recipient: `outreach+${record.id}@funding.mixitone.com`,
      sender: "Funder <funder@example.com>",
      subject: template.subject,
      "body-html": template.bodyHtml,
      "body-plain": template.bodyPlain,
    });
    const res = await fetch(`${baseUrl}/functions/v1/funding-email/inbound`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!res.ok) throw new Error(await res.text());
  }

  /** Set outreach status manually (e.g. after contacting a funder by phone). Saves the record. */
  async actionSetStatus(status: FundingOutreach["status"]): Promise<void> {
    const record = this.state.record as FundingOutreach | null;
    if (!record?.id) return;
    record.status = status;
    await record.save();
  }

  /** Set status to replied (e.g. after first funder reply). Saves the record. */
  async actionSetStatusReplied(): Promise<void> {
    const record = this.state.record as FundingOutreach | null;
    if (!record?.id) return;
    record.status = "replied";
    await record.save();
  }

  /** Set status to approved and record actual amount agreed. Saves the record. */
  async actionSetStatusApproved(amountAwardedCents: number): Promise<void> {
    const record = this.state.record as FundingOutreach | null;
    if (!record?.id) return;
    record.status = "approved";
    record.amount_awarded_cents = amountAwardedCents;
    await record.save();
  }

  /** Set status to declined. Saves the record. */
  async actionSetStatusDeclined(): Promise<void> {
    const record = this.state.record as FundingOutreach | null;
    if (!record?.id) return;
    record.status = "declined";
    await record.save();
  }

  /** Update potential amount (in pounds) and save. */
  async actionUpdatePotentialAmount(pounds: number | null): Promise<void> {
    const record = this.state.record as FundingOutreach | null;
    if (!record?.id) return;
    record.amount_potential_cents = pounds != null ? Math.round(pounds * 100) : undefined;
    await record.save();
  }

  /** Open compose modal with default subject/body from appeal. */
  async actionOpenCompose(): Promise<void> {
    const r = this.state.record as FundingOutreach | null;
    const appeal = r?.funding_appeal;
    this.state.pendingReviewMessage = false;
    this.state.composeSubject = appeal?.name ? `Funding request – ${appeal.name}` : "Funding request";
    const templateValues = r ? await this.templateValuesForOutreach(r) : {};
    this.state.composeMergeValues = templateValues;
    let templates: FundingAppealEmailTemplate[] = [];
    if (appeal?.id) {
      templates = await FundingAppealEmailTemplate.query()
        .eq("funding_appeal_id", appeal.id)
        .eq("is_deleted", false)
        .orderBy("created_at", "asc")
        .all();
    }
    this.state.emailTemplatesForCompose = templates;
    const first = templates[0];
    this.state.composeFundingAppealEmailTemplateId = first?.id ?? null;
    const baseHtml = first?.body_html ?? "";
    this.state.composeBody =
      first?.render_mode === "designer_document"
        ? baseHtml
        : applyFundingLetterTemplate(baseHtml, templateValues);
    this.state.replyingInViewModal = false;
    this.state.showComposeModal = true;
  }

  /** Close compose modal. */
  actionCloseCompose(): void {
    this.state.showComposeModal = false;
    this.state.composeMergeValues = {};
    this.state.emailTemplatesForCompose = [];
    this.state.composeFundingAppealEmailTemplateId = null;
  }

  /** Switch template vs one-off; body is re-merged with preview values for the current funder. */
  actionSetComposeTemplateSource(templateId: string | "__custom__"): void {
    const templates = this.state.emailTemplatesForCompose;
    if (templateId === "__custom__") {
      this.state.composeFundingAppealEmailTemplateId = null;
      this.state.composeBody = "";
      return;
    }
    const t = templates.find((x) => x.id === templateId);
    this.state.composeFundingAppealEmailTemplateId = templateId;
    this.state.composeBody =
      t?.render_mode === "designer_document"
        ? (t?.body_html ?? "")
        : applyFundingLetterTemplate(t?.body_html ?? "", this.state.composeMergeValues);
  }

  /** Open compose as reply to a subject; clear body and close viewing modal. */
  actionOpenReply(replyToSubject?: string): void {
    this.state.replyingInViewModal = false;
    this.state.composeSubject = replyToSubject?.trim() ? `Re: ${replyToSubject.trim()}` : "";
    this.state.composeBody = "";
    this.state.composeMergeValues = {};
    this.state.showComposeModal = true;
    this.state.viewingCommunication = null;
  }

  /** Start replying from the view-message modal; keeps the message visible. */
  async actionStartInlineReply(replyToSubject?: string): Promise<void> {
    const r = this.state.record as FundingOutreach | null;
    this.state.pendingReviewMessage = false;
    const sub = replyToSubject?.trim();
    this.state.composeSubject = sub ? `Re: ${sub}` : "Re:";
    this.state.composeBody = "";
    const templateValues = r ? await this.templateValuesForOutreach(r) : {};
    this.state.composeMergeValues = templateValues;
    this.state.replyingInViewModal = true;
  }

  /** Exit inline reply in the view modal and discard draft. */
  actionCancelInlineReply(): void {
    this.state.replyingInViewModal = false;
    this.state.composeBody = "";
    this.state.composeMergeValues = {};
  }

  actionSetComposeSubject(s: string): void {
    this.state.composeSubject = s;
  }

  actionSetComposeBody(s: string): void {
    this.state.composeBody = s;
  }

  actionSetViewingCommunication(comm: FundingOutreachCommunication | null): void {
    const prevId = this.state.viewingCommunication?.id;
    const nextId = comm?.id;
    if (comm === null) {
      this.state.replyingInViewModal = false;
      this.state.composeBody = "";
      this.state.composeMergeValues = {};
    } else if (nextId !== prevId) {
      this.state.replyingInViewModal = false;
    }
    this.state.viewingCommunication = comm;
  }

  private async templateValuesForOutreach(record: FundingOutreach): Promise<Record<string, string>> {
    await record.funder.load();
    await record.funding_appeal?.load();
    const funder = record.funder;
    const appeal = record.funding_appeal;
    const currency = Account.current?.currency ?? "usd";

    let clubName = "";
    let playerCount = "";
    let clubCountry: string | null = null;
    if (appeal?.club_id) {
      const club = await Club.query().find(appeal.club_id);
      clubName = club.name ?? "";
      clubCountry = club.country ?? null;
      playerCount = String(
        await ClubPlayer.query().eq("club_id", appeal.club_id).eq("is_deleted", false).count(),
      );
    }

    let sessionName = "";
    if (appeal?.tournament_session_id) {
      const session = await TournamentSession.query().find(appeal.tournament_session_id);
      sessionName = session.name ?? "";
    }

    let appealPageUrl = "";
    if (appeal?.id) {
      const landingPage = await FundingAppealLandingPage.query()
        .eq("funding_appeal_id", appeal.id)
        .eq("is_deleted", false)
        .first();
      if (landingPage) {
        const directoryBaseUrl = (import.meta.env.VITE_DIRECTORY_URL ?? "http://localhost:4321").replace(
          /\/+$/,
          "",
        );
        appealPageUrl =
          fundingAppealLandingPageDirectoryUrl({
            directoryBaseUrl,
            countryCode: clubCountry,
            landingPage,
          }) ?? "";
      }
    }

    return {
      funder_name: funder?.name ?? "",
      contact_name: funder?.contact_name ?? "",
      funder_contact_name: funder?.contact_name ?? "",
      club_name: clubName,
      appeal_name: appeal?.name ?? "",
      goal_notes: appeal?.goal_notes ?? "",
      appeal_target: appeal?.target_cents != null ? formatCurrencyCents(appeal.target_cents, currency) : "",
      session_name: sessionName,
      player_count: playerCount,
      appeal_page_url: appealPageUrl,
    };
  }

  actionSetPendingReviewMessage(show: boolean): void {
    this.state.pendingReviewMessage = show;
  }

  /** Open accept-amount modal and prefill from record. */
  actionOpenAcceptAmountModal(): void {
    const r = this.state.record as FundingOutreach | null;
    const potentialCents = r?.amount_potential_cents ?? null;
    const awardedCents = r?.amount_awarded_cents ?? null;
    const potentialPounds = potentialCents != null ? potentialCents / 100 : null;
    const awardedPounds = awardedCents != null ? awardedCents / 100 : null;
    this.state.acceptAmountPounds =
      awardedPounds != null ? String(awardedPounds) : potentialPounds != null ? String(potentialPounds) : "";
    this.state.showAcceptAmountModal = true;
  }

  actionCloseAcceptAmountModal(): void {
    this.state.showAcceptAmountModal = false;
  }

  actionSetAcceptAmountPounds(s: string): void {
    this.state.acceptAmountPounds = s;
  }

  /** Reassign the outreach to a different account user. Saves the record. */
  async actionSetAssignedTo(accountUserId: string | null): Promise<void> {
    const record = this.state.record;
    if (!record?.id) return;
    record.assigned_to_account_user_id = accountUserId ?? undefined;
    await record.save();
    const assigned = accountUserId ? this.state.accountUsers.find((au) => au.id === accountUserId) : null;
    if (assigned) record.assigned_to_account_user.set(assigned);
    else record.assigned_to_account_user.unset();
  }

  actionSetSimulateTemplateIndex(n: number): void {
    this.state.simulateTemplateIndex = n;
  }

  actionOpenAddMessageModal(): void {
    this.state.addMessageSource = "phone";
    this.state.addMessageSubject = "";
    this.state.addMessageBody = "";
    this.state.showAddMessageModal = true;
  }

  actionCloseAddMessageModal(): void {
    this.state.showAddMessageModal = false;
  }

  actionSetAddMessageSource(source: State["addMessageSource"]): void {
    this.state.addMessageSource = source;
  }

  actionSetAddMessageSubject(s: string): void {
    this.state.addMessageSubject = s;
  }

  actionSetAddMessageBody(s: string): void {
    this.state.addMessageBody = s;
  }

  @withStateToggle("addingMessage")
  async actionSubmitAddMessage(): Promise<void> {
    const record = this.state.record as FundingOutreach | null;
    if (!record?.id || !record.account_id || !record.club_id) return;
    const body = this.state.addMessageBody.trim();
    if (!body) return;
    const sourceLabel = MANUAL_MESSAGE_SOURCE_LABELS[this.state.addMessageSource];
    const subject = this.state.addMessageSubject.trim() || null;

    const comm = new FundingOutreachCommunication({
      account_id: record.account_id,
      club_id: record.club_id,
      funding_outreach_id: record.id,
      direction: "inbound",
      channel: "manual",
      from_email: "Manual",
      from_name: sourceLabel,
      to_email: "—",
      body_text: body,
      body_html: null,
      subject,
      sent_by_account_user_id: AccountUser.current?.id ?? undefined,
    });
    await comm.save();
    if (!comm.id) return;

    await flushTauriSyncAndWait();
    const result = await actions.funding.analyzeFundingCommunication({
      communicationId: comm.id,
    });

    const analysis = new FundingOutreachCommunicationAnalysis({
      account_id: record.account_id,
      funding_outreach_communication_id: comm.id,
      sentiment: result.sentiment,
      summary: result.summary ?? undefined,
      raw_response: result.raw_response != null ? (result.raw_response as unknown as object) : null,
    } as any);
    await analysis.save();

    // Refetch communications so we don't duplicate (realtime may also deliver the new row)
    const list = this.state.communications as List<FundingOutreachCommunication>;
    const commQuery = FundingOutreachCommunication.query()
      .eq("funding_outreach_id", record.id)
      .eq("is_deleted", false)
      .orderBy("occurred_at", "asc")
      .preload("sent_by_account_user");
    const fresh = await commQuery.all();
    list.setItems(fresh as FundingOutreachCommunication[]);
    await this.refetchAnalysesForOutreach();

    if (record.amount_potential_cents == null && result.amount_cents != null && result.amount_cents > 0) {
      record.amount_potential_cents = result.amount_cents;
      await record.save();
    }

    if (fundingOutreachShouldAdvanceToReplied(record.status)) {
      record.status = "replied";
      await record.save();
    }

    this.state.showAddMessageModal = false;
    this.state.addMessageSubject = "";
    this.state.addMessageBody = "";
  }

  override async changeProps(newProps: Props) {
    // Use state.id, not this.props.id: _props is only set inside super.changeProps, so first init has no props yet.
    const previousId = this.state.id;
    await super.changeProps({
      ...newProps,
      attributes: newProps.attributes ?? {},
    } as any);
    if (newProps.id !== previousId) {
      this.state.editingFunder = false;
      this.state.replyingInViewModal = false;
    }
  }

  override async saveRecord(record: FundingOutreach): Promise<void> {
    const r = record as FundingOutreach & {
      new_funder_name?: string;
      new_funder_type?: string;
      new_funder_contact_name?: string;
      new_funder_contact_email?: string;
      new_funder_contact_phone?: string;
      new_funder_note?: string;
    };
    if (!r.account_id) r.account_id = Account.current!.id!;
    if (!r.club_id) r.club_id = this.props.clubId;
    const isAddFunderOnly = Boolean(this.props.appealId && this.state.newRecord);
    if (this.props.appealId) r.funding_appeal_id = this.props.appealId;

    if (r.funder_id === ADD_NEW_FUNDER_VALUE) {
      const funder = new Funder({
        club_id: this.props.clubId,
        account_id: Account.current!.id!,
        name: r.new_funder_name ?? "",
        funder_type: (r.new_funder_type ?? "council") as
          | "council"
          | "trust"
          | "business"
          | "individual"
          | "organisation",
        contact_name: r.new_funder_contact_name ?? "",
        contact_email: r.new_funder_contact_email ?? "",
        contact_phone: r.new_funder_contact_phone ?? "",
        note: r.new_funder_note ?? "",
        created_by_account_user_id: AccountUser.current?.id ?? undefined,
      });
      await funder.save();
      r.funder_id = funder.id;
    }

    if (isAddFunderOnly) {
      const appeal = await FundingAppeal.query().find(this.props.appealId!);
      r.purpose_label = appeal.name ?? "Appeal";
      r.status = "to_contact";
      r.amount_requested_cents = undefined;
      r.amount_potential_cents = undefined;
      r.amount_awarded_cents = undefined;
    }
    if (this.state.newRecord && AccountUser.current?.id) {
      const rOutreach = r as FundingOutreach & {
        created_by_account_user_id?: string;
        assigned_to_account_user_id?: string;
      };
      rOutreach.created_by_account_user_id = AccountUser.current.id;
      rOutreach.assigned_to_account_user_id = AccountUser.current.id;
    }
    // amount_*_cents are bound directly by the form (pounds field type)

    await record.save();
  }

  /** Refetch analyses for current inbound communications and update state (used after realtime events). */
  private async refetchAnalysesForOutreach(): Promise<void> {
    const inbounds = (this.state.communications as List<FundingOutreachCommunication>).filter(
      (c: FundingOutreachCommunication) => c.direction === "inbound",
    );
    const inboundIds = inbounds.map((c: FundingOutreachCommunication) => c.id).filter(Boolean) as string[];
    if (inboundIds.length === 0) return;
    const analyses = await FundingOutreachCommunicationAnalysis.query()
      .in("funding_outreach_communication_id", inboundIds)
      .eq("is_deleted", false)
      .all();
    this.state.analysesByCommId = Object.fromEntries(
      (analyses as FundingOutreachCommunicationAnalysis[]).map((a) => [
        a.funding_outreach_communication_id,
        {
          sentiment: a.sentiment ?? "unknown",
          summary: a.summary ?? null,
        },
      ]),
    );
    const latestInbound = inbounds.reduce<FundingOutreachCommunication | null>(
      (best, c) => (!best || String(c.occurred_at) > String(best.occurred_at) ? c : best),
      null,
    );
    if (latestInbound?.id) {
      const analysis = await FundingOutreachCommunicationAnalysis.query()
        .eq("funding_outreach_communication_id", latestInbound.id)
        .eq("is_deleted", false)
        .first();
      this.state.latestReplyAnalysis = analysis ?? null;
    }
  }

  override async onLoad(): Promise<void> {
    const clubId = this.props.clubId;
    if (clubId) {
      const club = await Club.query().find(clubId);
      const funders = await club.funders.query.eq("is_deleted", false).all();
      this.setState({ funders });
    } else {
      this.setState({ funders: [] });
    }

    if (this.props.appealId && this.state.newRecord) {
      const existing = await FundingOutreach.query()
        .eq("funding_appeal_id", this.props.appealId)
        .eq("is_deleted", false)
        .all();
      const funderIdsAlreadyOnAppeal = existing
        .map((o) => o.funder_id)
        .filter((id): id is string => Boolean(id));
      this.setState({ funderIdsAlreadyOnAppeal });
    } else {
      this.setState({ funderIdsAlreadyOnAppeal: [] });
    }

    const { record, newRecord } = this.state;
    const accountId =
      record?.account_id ??
      (this.props.clubId ? (await Club.query().find(this.props.clubId)).account_id : null);
    if (accountId) {
      const accountUsers = await AccountUser.query()
        .eq("account_id", accountId)
        .eq("is_deleted", false)
        .orderBy("name")
        .all();
      this.setState({ accountUsers });
    } else {
      this.setState({ accountUsers: [] });
    }

    this.state.latestReplyAnalysis = null;
    if (record && !newRecord && record.id) {
      const outreachId = record.id;
      if (outreachId) {
        const commQuery = FundingOutreachCommunication.query()
          .eq("funding_outreach_id", outreachId)
          .eq("is_deleted", false)
          .orderBy("occurred_at", "asc")
          .preload("sent_by_account_user");
        const initial = await commQuery.all();
        const list = new List<FundingOutreachCommunication>(...initial);
        this.state.communications = list;
        this.addDependency("communications", await subscribeList(list, commQuery));
        await this.refetchAnalysesForOutreach();

        const analysisQuery = FundingOutreachCommunicationAnalysis.query().eq("is_deleted", false);
        const removeAnalyses = await analysisQuery.subscribe({
          callback: (payloads) => {
            for (const p of payloads) {
              const commId = (p.model as FundingOutreachCommunicationAnalysis)
                ?.funding_outreach_communication_id;
              if (!commId) continue;
              const commIds = (this.state.communications as List<FundingOutreachCommunication>).map(
                (c: FundingOutreachCommunication) => c.id,
              );
              if (!commIds.includes(commId)) continue;
              void this.refetchAnalysesForOutreach();
              break;
            }
          },
        });
        this.addDependency("analysesRealtime", removeAnalyses);
      }
    }
  }

  /** When adding from an appeal, only choose/create funder; purpose and amount come from the appeal workflow. */
  get addFunderOnly(): boolean {
    return Boolean(this.props.appealId && this.state.newRecord);
  }

  fields(): Record<string, FormFieldConfig> {
    const funderOptions = [
      ...this.state.funders.map((f) => ({
        value: f.id!,
        label: f.name ?? "",
      })),
      ...(this.state.newRecord ? [{ value: ADD_NEW_FUNDER_VALUE, label: "+ Add new funder" }] : []),
    ];
    const whenAddingNewFunder = (record: FundingOutreach | null) =>
      record?.funder_id === ADD_NEW_FUNDER_VALUE;
    const allFields: Record<string, FormFieldConfig> = {
      funder_id: {
        type: "select",
        name: "funder_id",
        label: "Funder",
        required: true,
        options: funderOptions,
        group: "create",
      },
      new_funder_name: {
        type: "text",
        name: "new_funder_name",
        label: "New funder name",
        required: (record) => whenAddingNewFunder(record),
        condition: whenAddingNewFunder,
        group: "create",
      },
      new_funder_type: {
        type: "select",
        name: "new_funder_type",
        label: "New funder type",
        options: FUNDER_TYPE_SELECT_OPTIONS,
        condition: whenAddingNewFunder,
        group: "create",
      },
      new_funder_contact_name: {
        type: "text",
        name: "new_funder_contact_name",
        label: "New funder contact name",
        condition: whenAddingNewFunder,
        group: "create_extra",
      },
      new_funder_contact_email: {
        type: "text",
        name: "new_funder_contact_email",
        label: "New funder contact email",
        inputType: "email",
        condition: whenAddingNewFunder,
        group: "create_extra",
      },
      new_funder_contact_phone: {
        type: "text",
        name: "new_funder_contact_phone",
        label: "New funder contact phone",
        inputType: "tel",
        condition: whenAddingNewFunder,
        group: "create_extra",
      },
      new_funder_note: {
        type: "text",
        name: "new_funder_note",
        label: "New funder note",
        inputType: "textarea",
        condition: whenAddingNewFunder,
        group: "create_extra",
      },
      purpose_label: {
        type: "text",
        name: "purpose_label",
        label: "Purpose",
        required: true,
        group: "create",
      },
      amount_requested_cents: {
        type: "dollars",
        name: "amount_requested_cents",
        label: "Amount requested",
        min: 0,
        step: 0.01,
        group: "details",
      },
      amount_potential_cents: {
        type: "dollars",
        name: "amount_potential_cents",
        label: "Indicated amount",
        min: 0,
        step: 0.01,
        group: "details",
      },
      amount_awarded_cents: {
        type: "dollars",
        name: "amount_awarded_cents",
        label: "Amount awarded",
        min: 0,
        step: 0.01,
        group: "details",
      },
      status: {
        type: "select",
        name: "status",
        label: "Status",
        options: FUNDING_OUTREACH_STATUS_SELECT_OPTIONS,
        group: "details",
      },
    } as const satisfies Record<string, FormFieldConfig>;
    if (this.addFunderOnly) {
      const keys = [
        "funder_id",
        "new_funder_name",
        "new_funder_type",
        "new_funder_contact_name",
        "new_funder_contact_email",
        "new_funder_contact_phone",
        "new_funder_note",
      ];
      const out: Record<string, FormFieldConfig> = {};
      for (const k of keys) out[k] = allFields[k];
      return out;
    }
    return allFields;
  }
}

export { FundingOutreachFormController };
